/**
 * Client-side allowlist HTML sanitizer for a manuscript chapter `body`.
 *
 * WHY THIS EXISTS (do not delete without re-verifying the server side):
 * `ManuscriptEditor.tsx` documents `body` as "server-side sanitized
 * (`isomorphic-dompurify`) before persist" (citing CODE_GUIDELINES §26), but
 * `apps/server/src/editor/handlers.ts`'s `handleEditorSave` inserts
 * `payload.content` into `document_versions` verbatim — no sanitize call
 * anywhere on that path. That means a chapter `body` reaching this bundle
 * (via `document.get`/`version.get`/`discover.get`/`link.resolve`) is NOT
 * guaranteed safe HTML: it may be plain text (generate/expand/translate
 * origin) or F67-WYSIWYG-produced markup that was never actually purified
 * server-side. Rendering it with `dangerouslySetInnerHTML` unsanitized would
 * be a stored-XSS hole reachable by every anonymous visitor of a public
 * share (F62/F63) or link (F63) the moment any editor-role collaborator (or
 * a compromised/malicious one) saves a crafted `<script>`/`onerror=`
 * payload. `isomorphic-dompurify` isn't installed for `@xdocs/web` (only
 * `@xdocs/core`, server-side) and adding it would need a new dependency +
 * `approved-libs.ts` edit outside this fix's scope — so this sanitizes with
 * the DOM's own parser (`DOMParser`, no new dependency, same
 * no-second-parser posture as `HighlightedSnippet.tsx`), reducing the
 * WYSIWYG's actual tag surface (bold/italic, headings, lists, blockquote,
 * links — see `EditorToolbar.tsx`'s `execCommand` calls) to those tags only,
 * stripping every attribute except a validated `href` on `<a>`, and
 * dropping `<script>`/`<style>`/`<iframe>`/embeds entirely (subtree
 * included). Plain text with no markup round-trips unchanged.
 */

const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI',
  'P',
  'DIV',
  'BR',
  'A',
  'SPAN',
]);

/** Dropped WITH their entire subtree — unlike an unknown/disallowed tag (which is
 * unwrapped, keeping its text), these never have display-worthy text content. */
const DROP_SUBTREE_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'SVG', 'MATH', 'LINK', 'META']);

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;
const ALLOWED_SCHEME_RE = /^(https?:|mailto:|tel:)/i;

/** `null` rejects the href outright (`javascript:`, `data:`, `vbscript:`, ...);
 * relative/fragment URLs (no scheme) and the allowlisted schemes pass through. */
function sanitizeHref(rawHref: string): string | null {
  const trimmed = rawHref.trim();
  if (trimmed.length === 0) return null;
  if (SCHEME_RE.test(trimmed) && !ALLOWED_SCHEME_RE.test(trimmed)) return null;
  return trimmed;
}

function sanitizeChildren(source: Node, target: Node, doc: Document): void {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return; // drop comments/CDATA/etc.
    const el = child as Element;
    const tag = el.tagName;

    if (DROP_SUBTREE_TAGS.has(tag)) return;

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap an unknown/disallowed element — keep its (recursively
      // sanitized) children so ordinary text isn't lost, just its own tag.
      sanitizeChildren(el, target, doc);
      return;
    }

    const clean = doc.createElement(tag.toLowerCase());
    if (tag === 'A') {
      const href = el.getAttribute('href');
      const safeHref = href ? sanitizeHref(href) : null;
      if (safeHref) {
        clean.setAttribute('href', safeHref);
        clean.setAttribute('rel', 'noopener noreferrer');
        clean.setAttribute('target', '_blank');
      }
    }
    sanitizeChildren(el, clean, doc);
    target.appendChild(clean);
  });
}

/**
 * Sanitizes a manuscript chapter `body` down to the WYSIWYG editor's actual
 * tag surface before it's ever passed to `dangerouslySetInnerHTML`. Safe to
 * call on plain text (no markup) — it round-trips unchanged.
 */
export function sanitizeManuscriptHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const container = doc.createElement('div');
  sanitizeChildren(doc.body, container, doc);
  return container.innerHTML;
}
